<template>
	<div class="min-h-screen bg-background">
		<Transition
			enter-active-class="transition-opacity duration-300 ease-out"
			leave-active-class="transition-opacity duration-200 ease-in"
			enter-from-class="opacity-0"
			leave-to-class="opacity-0"
		>
			<div
				v-if="showMobileMenu"
				class="fixed inset-0 bg-primary-dark/60 backdrop-blur-sm z-40 lg:hidden"
				@click="closeMobileMenu"
			/>
		</Transition>

		<Transition
			enter-active-class="transition-all duration-300 ease-out"
			leave-active-class="transition-all duration-200 ease-in"
			enter-from-class="opacity-0 backdrop-blur-none scale-95"
			leave-to-class="opacity-0 backdrop-blur-none scale-105"
		>
			<div
				v-if="showAnyLoader"
				class="fixed inset-0 bg-white/95 backdrop-blur-md z-50 flex items-center justify-center p-4"
			>
				<div
					class="bg-white shadow-2xl rounded-3xl p-8 border border-border max-w-sm w-full mx-4 relative overflow-hidden"
				>
					<div
						class="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-surface"
					></div>

					<div class="relative z-10">
						<div class="flex items-center justify-center mb-8">
							<div class="relative">
								<div
									class="w-16 h-16 border-4 border-gray-200 border-t-primary rounded-full animate-spin"
								></div>
								<div
									class="absolute inset-2 w-12 h-12 border-4 border-transparent border-t-primary-light/60 rounded-full animate-spin animate-reverse"
								></div>
								<div
									class="absolute inset-6 w-4 h-4 bg-primary rounded-full animate-pulse"
								></div>
							</div>
						</div>

						<div class="text-center">
							<h3
								class="text-text-primary font-bold text-xl mb-3 tracking-tight"
							>
								{{ loaderText }}
							</h3>
							<p class="text-text-secondary text-sm mb-6">
								Please wait while we prepare your dashboard
							</p>

							<div
								class="w-full h-2 bg-border rounded-full overflow-hidden"
							>
								<div
									class="h-full bg-gradient-to-r from-primary to-primary-light rounded-full transition-all duration-1000 ease-out"
									:style="{ width: loadingProgress + '%' }"
								></div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Transition>

		<template v-if="!initialLoading">
			<DashNav
				:is-menu-open="showMobileMenu"
				:is-collapsed="isCollapsed"
				@toggle-mobile-menu="toggleMobileMenu"
				@toggle-sidebar="toggleSidebar"
			/>

			<RequestIPF />

			<div class="flex min-h-screen pt-16">
				<SideNav
					v-if="isMobile ? showMobileMenu : true"
					:is-mobile="isMobile"
					:is-collapsed="isCollapsed"
					:show-mobile-menu.sync="showMobileMenu"
				/>

				<main
					class="flex-1 transition-all duration-300 ease-in-out relative"
					:class="mainContentClasses"
				>
					<div class="flex-1 relative min-h-screen">
						<Transition
							name="page"
							mode="out-in"
							enter-active-class="transition-all duration-400 ease-out"
							leave-active-class="transition-all duration-300 ease-in"
							enter-from-class="opacity-0 transform translate-y-6 scale-95"
							leave-to-class="opacity-0 transform -translate-y-4 scale-105"
						>
							<div :key="$route.fullPath" class="w-full relative">
								<div class="p-6 sm:p-8 w-full">
									<router-view class="w-full" />
								</div>
							</div>
						</Transition>
					</div>
				</main>
			</div>

			<div
				v-if="isMobile"
				class="fixed bottom-6 left-6 z-30 flex flex-col gap-3 items-start"
			>
				<Transition
					enter-active-class="transition-all duration-300 ease-out delay-100"
					leave-active-class="transition-all duration-200 ease-in"
					enter-from-class="opacity-0 scale-90 translate-y-2"
					leave-to-class="opacity-0 scale-90 translate-y-2"
				>
					<div>
						<button
							@click="openHelp"
							@keyup.enter="openHelp"
							class="group relative bg-primary hover:bg-primary-light text-white w-12 h-12 flex items-center justify-center rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/50"
							aria-label="Get help and support"
							title="Help & Support"
						>
							<svg
								class="w-5 h-5 transition-transform duration-200 group-hover:rotate-12"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<span class="sr-only">Open help center</span>
							<div
								class="absolute inset-0 bg-primary rounded-full blur-lg opacity-30 -z-10"
							></div>
						</button>
					</div>
				</Transition>
			</div>

			<div
				v-else
				class="fixed bottom-6 right-6 z-30 flex flex-col gap-3 items-end"
			>
				<Transition
					enter-active-class="transition-all duration-300 ease-out delay-100"
					leave-active-class="transition-all duration-200 ease-in"
					enter-from-class="opacity-0 scale-90 translate-y-2"
					leave-to-class="opacity-0 scale-90 translate-y-2"
				>
					<div>
						<button
							@click="openHelp"
							@keyup.enter="openHelp"
							class="inline-flex items-center gap-3 bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-102 active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/50"
							aria-label="Open help center"
							title="Help & Support"
						>
							<svg
								class="w-5 h-5 transition-transform duration-200"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<span class="text-sm font-medium"
								>Help & Support</span
							>
						</button>
					</div>
				</Transition>

				<Transition
					enter-active-class="transition-all duration-300 ease-out"
					leave-active-class="transition-all duration-200 ease-in"
					enter-from-class="opacity-0 scale-90 translate-y-2"
					leave-to-class="opacity-0 scale-90 translate-y-2"
				>
					<div>
						<button
							@click="scrollToTop"
							@keyup.enter="scrollToTop"
							class="inline-flex items-center gap-3 bg-white hover:bg-surface text-text-primary hover:text-primary px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-102 active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/20 border border-border"
							aria-label="Back to top"
							title="Back to top"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 11l5-5m0 0l5 5m-5-5v12"
								/>
							</svg>
							<span class="text-sm font-medium">Back to top</span>
						</button>
					</div>
				</Transition>
			</div>

			<div
				v-if="showScrollTop && isMobile"
				class="fixed bottom-6 right-6 z-30 flex flex-col gap-3 items-end"
			>
				<Transition
					enter-active-class="transition-all duration-300 ease-out"
					leave-active-class="transition-all duration-200 ease-in"
					enter-from-class="opacity-0 scale-90 translate-y-2"
					leave-to-class="opacity-0 scale-90 translate-y-2"
				>
					<div>
						<button
							@click="scrollToTop"
							@keyup.enter="scrollToTop"
							class="group relative bg-white hover:bg-surface text-text-primary w-12 h-12 flex items-center justify-center rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/20 border border-border"
							aria-label="Back to top"
							title="Back to top"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 11l5-5m0 0l5 5m-5-5v12"
								/>
							</svg>
							<span class="sr-only">Back to top</span>
						</button>
					</div>
				</Transition>
			</div>

			<footer class="bg-white border-t border-border">
				<ReferProduct />
			</footer>
		</template>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
	FetchClientInvoices,
	FetchClientPolicies,
	FetchClientQuotations,
	FetchWallets,
} from './api/portal';
import { FetchIntermediaries } from '../insurance/api/insurance';

export default {
	name: 'DashLayout',
	data() {
		return {
			showMobileMenu: false,
			isCollapsed: false,
			initialLoading: true,
			routeLoading: false,
			componentLoading: false,
			loaderText: 'Loading dashboard...',
			loadingProgress: 0,
			windowWidth: 0,
			resizeTimeout: null,
			showScrollTop: false,
			scrollTopThreshold: 300,
		};
	},
	computed: {
		showAnyLoader() {
			return (
				this.initialLoading ||
				this.routeLoading ||
				this.componentLoading
			);
		},
		isMobile() {
			return this.windowWidth < 768;
		},
		mainContentClasses() {
			if (this.isMobile) {
				return 'w-full';
			}
			return this.isCollapsed ? 'ml-20' : 'ml-72';
		},
		helpButtonStyle() {
			// On mobile keep the help button to the left; on desktop position it from the right
			if (this.isMobile) {
				return { left: '1.5rem' };
			}
			return { right: this.isCollapsed ? '104px' : '312px' };
		},
		helpContainerClass() {
			// Align items to the start on mobile (left), and to the end on desktop (right)
			return this.isMobile ? 'items-start' : 'items-end';
		},
		userDisplayName() {
			return (
				(this.selectAuthData && this.selectAuthData.firstName) ||
				(this.selectKycData && this.selectKycData.firstName) ||
				'User'
			);
		},
		...mapGetters('auth', ['isAuthenticated', 'selectAuthData']),
		...mapGetters('kyc', ['selectKycData']),
		...mapGetters('pagination', ['currentPage']),
	},
	components: {
		ReferProduct: () => import('@/components/newportal/main/refer-product'),
		RequestIPF: () =>
			import('@/components/newportal/templates/others/request-ipf'),
		SideNav: () => import('./side-nav'),
		DashNav: () => import('./dashnav'),
	},
	methods: {
		...mapActions('quotations', [
			'setClientQuotationData',
			'setClientPoliciesData',
		]),
		...mapActions('invoices', ['setClientInvoicesData', 'setClientWallet']),
		...mapActions('help', ['setEntities']),

		handleResize() {
			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => {
				this.windowWidth = window.innerWidth;
				if (!this.isMobile && this.showMobileMenu) {
					this.showMobileMenu = false;
				}
			}, 100);
		},

		handleScroll() {
			this.showScrollTop = window.pageYOffset > this.scrollTopThreshold;
		},

		scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		},

		toggleMobileMenu() {
			this.showMobileMenu = !this.showMobileMenu;
		},

		toggleSidebar() {
			this.isCollapsed = !this.isCollapsed;
		},

		closeMobileMenu() {
			if (this.showMobileMenu) {
				this.showMobileMenu = false;
			}
		},

		setComponentLoading(loading, text = 'Loading...') {
			this.componentLoading = loading;
			if (loading) {
				this.loaderText = text;
				this.startLoadingProgress();
			} else {
				this.loadingProgress = 100;
				setTimeout(() => {
					this.loadingProgress = 0;
				}, 500);
			}
		},

		startLoadingProgress() {
			this.loadingProgress = 0;
			const interval = setInterval(() => {
				if (
					this.loadingProgress < 90 &&
					(this.componentLoading || this.initialLoading)
				) {
					this.loadingProgress += Math.random() * 15;
				} else {
					clearInterval(interval);
				}
			}, 200);
		},

		async openHelp() {
			try {
				this.setComponentLoading(true, 'Opening help center...');
				const res = await FetchIntermediaries();
				if (res.status) {
					this.setEntities(res.data);
					this.$router.push({ name: 'HelpMotor' });
				}
			} catch (error) {
				console.error('Error fetching intermediaries:', error);
				this.showNotification('error', 'Unable to open help center');
				this.$router.push({ name: 'Home' });
			} finally {
				this.setComponentLoading(false);
			}
		},

		async fetchClientQuotations() {
			try {
				const fetchData = { querryOptions: { page: 1 } };
				const res = await FetchClientQuotations(fetchData);
				if (res.status) {
					this.setClientQuotationData(res.data[0].items);
				}
			} catch (error) {
				console.error('Error fetching quotations:', error);
				this.handleApiError(error, 'Failed to load quotations');
			}
		},

		async fetchClientPolicies() {
			try {
				const fetchData = { querryOptions: { page: 1 } };
				const res = await FetchClientPolicies(fetchData);
				if (res.data[0].items.length > 0) {
					this.setClientPoliciesData(res.data[0].items);
				} else {
					this.setClientPoliciesData([]);
				}
			} catch (error) {
				console.error('Error fetching policies:', error);
			}
		},

		async fetchClientInvoices() {
			try {
				const fetchData = { querryOptions: { page: 1 } };
				const res = await FetchClientInvoices(fetchData);
				if (res.status) {
					this.setClientInvoicesData(res.data[0].items);
				}
			} catch (error) {
				console.error('Error fetching invoices:', error);
				this.handleApiError(error, 'Failed to load invoices');
			}
		},

		async fetchClientWallet() {
			try {
				const res = await FetchWallets();
				if (res.status) {
					this.setClientWallet(res.data);
				}
			} catch (error) {
				console.error('Error fetching wallet:', error);
				this.handleApiError(error, 'Failed to load wallet');
			}
		},

		async fetchAllData() {
			try {
				this.initialLoading = true;
				this.loaderText = 'Setting up your dashboard...';
				this.startLoadingProgress();
				const tasks = [
					{ fn: this.fetchClientQuotations, name: 'quotations' },
					{ fn: this.fetchClientPolicies, name: 'policies' },
					{ fn: this.fetchClientInvoices, name: 'invoices' },
					{ fn: this.fetchClientWallet, name: 'wallet' },
				];
				for (const task of tasks) {
					this.loaderText = `Loading ${task.name}...`;
					await task.fn();
					this.loadingProgress = Math.min(
						this.loadingProgress + 20,
						90,
					);
				}
				this.loadingProgress = 100;
			} catch (error) {
				console.error('Error loading dashboard:', error);
				this.showNotification('error', 'Some data failed to load');
			} finally {
				setTimeout(() => {
					this.initialLoading = false;
				}, 300);
			}
		},

		showNotification(type, message, title = null) {
			Event.$emit('notifications:show', {
				type,
				title: title || (type === 'success' ? 'Success' : 'Error'),
				message,
				duration: type === 'error' ? 5000 : 3000,
			});
		},

		handleApiError(error, defaultMessage) {
			const errorMessage =
				(error.response &&
					error.response.data &&
					error.response.data.errors) ||
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				defaultMessage;
			// this.showNotification('error', errorMessage);
		},
	},

	async mounted() {
		this.windowWidth = window.innerWidth;
		window.addEventListener('resize', this.handleResize);
		window.addEventListener('scroll', this.handleScroll);
		await this.fetchAllData();
		this.$router.beforeEach((to, from, next) => {
			if (to.name !== from.name) {
				this.routeLoading = true;
				this.loaderText = 'Navigating...';
				if (this.isMobile) {
					this.showMobileMenu = false;
				}
			}
			next();
		});
		this.$router.afterEach(() => {
			setTimeout(() => {
				this.routeLoading = false;
			}, 150);
		});
		Event.$on('layout:loading:start', (text) => {
			this.setComponentLoading(true, text);
		});
		Event.$on('layout:loading:stop', () => {
			this.setComponentLoading(false);
		});
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize);
		window.removeEventListener('scroll', this.handleScroll);
		clearTimeout(this.resizeTimeout);
		Event.$off('layout:loading:start');
		Event.$off('layout:loading:stop');
	},
};
</script>

<style scoped>
.page-enter-active,
.page-leave-active {
	transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.page-enter-from {
	opacity: 0;
	transform: translateY(20px) scale(0.98);
}
.page-leave-to {
	opacity: 0;
	transform: translateY(-10px) scale(1.02);
}
html {
	scroll-behavior: smooth;
}
@keyframes slideInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
.animate-slideInUp {
	animation: slideInUp 0.6s ease-out;
}
@keyframes rotate-reverse {
	from {
		transform: rotate(360deg);
	}
	to {
		transform: rotate(0deg);
	}
}
.animate-reverse {
	animation: rotate-reverse 1.5s linear infinite;
}
@supports not (backdrop-filter: blur(12px)) {
	.backdrop-blur-sm {
		background-color: rgba(255, 255, 255, 0.9);
	}
	.backdrop-blur-md {
		background-color: rgba(255, 255, 255, 0.95);
	}
}
</style>
